import React from 'react';
import { useController } from 'react-hook-form';
import { FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { EmployeesBlock as EmployeesBlockModel } from '../../../../declarations/models/blocks/EmployeesBlock';
import Container from '../../../../components/Container';
import CheckboxInput from '../../../../components/forms/CheckboxInput';
import Styles from '../../../../assets/js/Styles';
import SwitchInput from '../../../../components/forms/SwitchInput';
import SelectInput from '../../../../components/forms/SelectInput';
import { SortDirection, SortType } from '../../../../declarations/models/SortOption';
import SiteSharingBlockSettings from '../SiteSharingBlockSettings';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

export const settingsAreaStyle = {
  width: '100%',
  backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
  padding: 2,
  borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
  gap: 2,
};

export const EMPLOYEES_TOGGLE_FIELDS = ['Email', 'Phone', 'Links', 'Position', 'Department', 'Site', 'Description'];

const SORT_OPTIONS = [SortType.LAST_NAME, SortType.FIRST_NAME, SortType.DEPARTMENT];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getToggleFieldsSettings = (t: any, blockPath: BlockPath) => (
  <OutlinedContainer fullWidth column left label={t('EmployeesBlock.SelectInfoToShow')}>
    <FormGroup row>
      {EMPLOYEES_TOGGLE_FIELDS.map((field) => (
        <CheckboxInput
          key={field}
          path={`${blockPath}.show${field}`}
          label={t(`EmployeesBlock.Show${field}`)}
          fullWidth={false}
        />
      ))}
    </FormGroup>
  </OutlinedContainer>
);

export const EmployeesAutoBlock: BlockComponent = () => {
  const { t: tComp } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as EmployeesBlockModel;
  const mobileGridOptions = ['auto', 'horizontal', 'list']; // 'card' option is intentionally left out for grid view, as it doesn't make sense to have card view in a grid layout
  const mobileListOptions = ['auto', 'card'];
  const {
    field: { value: siteIds, onChange: setSiteIds },
  } = useController<BlockSpecificPage<EmployeesBlockModel>, `${BlockPath}.siteIds`>({ name: `${blockPath}.siteIds` });

  return (
    <Container column top gap={2} left fullWidth>
      <Container sx={settingsAreaStyle}>
        <SiteSharingBlockSettings siteIds={siteIds} setSiteIds={setSiteIds} />
      </Container>
      {getToggleFieldsSettings(tComp, blockPath)}

      <OutlinedContainer fullWidth column left label={tComp('KPBlock.DisplayOptionsModuleLabel')}>
        <Container my={1} fullWidth left gap={2}>
          <SelectInput
            options={SORT_OPTIONS}
            getOptionKey={(o) => o}
            getOptionLabel={(o) => tCommon(`SortTypes.${o}`)}
            path={`${blockPath}.sortBy`}
            label={tCommon('SortTypeLabel')}
            defaultValue={SortType.LAST_NAME}
            size='small'
            hideNoSelectionOption
          />
          <SelectInput
            options={Object.values(SortDirection)}
            getOptionKey={(o) => o}
            getOptionLabel={(o) => tCommon(`SortDirections.${o}`)}
            path={`${blockPath}.order`}
            label={tCommon('SortDirectionLabel')}
            defaultValue={SortDirection.ASC}
            size='small'
            hideNoSelectionOption
          />
          {currentBlock.view?.includes('grid') && (
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileGridOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => tComp(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={tComp('CardModuleBlock.GridMobileBehaviourLabel')}
            />
          )}
        </Container>
        <Container my={1} fullWidth left gap={2}>
          <div style={{ width: '250px' }}>
            <SwitchInput
              fullWidth={false}
              path={`${blockPath}.enableSearch`}
              label={tComp('EmployeesBlock.EnableSearch')}
            />
          </div>

          {currentBlock.enableSearch && (
            <SelectInput
              options={['local', 'page', 'site']}
              getOptionKey={(o) => o}
              getOptionLabel={(o) => tComp(`EmployeesBlock.SearchTypeOptions.${o}`)}
              path={`${blockPath}.searchType`}
              label={tComp('EmployeesBlock.SearchTypeLabel')}
              defaultValue='local'
              size='small'
              hideNoSelectionOption
            />
          )}
          {currentBlock.view?.includes('_____list') && (
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileListOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => tComp(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={tComp('CardModuleBlock.ListMobileBehaviourLabel')}
            />
          )}
        </Container>
      </OutlinedContainer>
    </Container>
  );
};

export default EmployeesAutoBlock;
